import React, { useState, useEffect } from "react";
import { withRouter } from "next/router";
import CheckoutPage from "../../src/pages/Checkout/CheckoutPage";
import { getEventDetails } from "../../src/crud/checkout.crud";
import Head from "next/head";
import { NextSeo } from "next-seo";
import { convertToPlainSeo, eventUrlValidation } from "../../src/helpers/common";
import { baseUrl, GoogleAnalyticsId } from "../../src/config/config";
import { getQuery, initGA } from "../../src/helpers/utils";

const Checkout = ({ ...props }) => {
	const [gtmID, setGtmID] = useState(null);
	const [pixelID, setPixelID] = useState(null);
	const [gaID, setGaID] = useState(null);

	useEffect(() => {
		// Fetch GAID, GTMID, and PixelID from the event data
		const fetchedGAID = props.eventData.google_analytics_code;
		const fetchedGTMID = props.eventData.gtm_code; // Example property
		const fetchedPixelID = props.eventData.facebook_pixel_code; // Example property

		if (fetchedGAID) {
			initGA([fetchedGAID, GoogleAnalyticsId]); // Add your global GA ID
			setGaID(fetchedGAID);
		}

		if (fetchedGTMID) {
			setGtmID(fetchedGTMID);
		}

		if (fetchedPixelID) {
			setPixelID(fetchedPixelID);
		}
	}, [props.eventData]);

	return (
		<>
			<NextSeo
				title={`Register for ${props.eventData.name}`}
				titleTemplate="%s"
				description={
					props.eventData.description
						? convertToPlainSeo(props.eventData.description)
						: ""
				}
				canonical={`${baseUrl}/${props.eventData.event_url}`}
				openGraph={{
					url: `${baseUrl}/${props.eventData.event_url}`,
					title: `Register for ${props.eventData.name}`,
					description: props.eventData.description
						? convertToPlainSeo(props.eventData.description)
						: "",
					site_name: "KonfHub",
					images: [
						{
							url: props.eventData.poster_thumbnail
								? props.eventData.poster_thumbnail
								: props.eventData.event_poster_url
									? props.eventData.event_poster_url
									: "",
							width: 800,
							height: 600,
							alt: "KonfHub",
							type: "image/jpeg"
						}
					]
				}}
			/>
			{props.eventData.event_template && (
				<Head>
					<link
						href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Montserrat:wght@200;300&display=swap"
						rel="stylesheet"
					/>
					{/* Google Analytics */}
					{
						gaID && (
							<>
								{/*<!-- Google tag (gtag.js) -->*/}
								<script async src={`https://www.googletagmanager.com/gtag/js?id=${gaID}`}></script>
								{/* GA Script */}
								<script
									dangerouslySetInnerHTML={{
										__html: `
										window.dataLayer = window.dataLayer || [];
            							function gtag(){dataLayer.push(arguments);}
            							gtag('js', new Date());
            							gtag('config', '${gaID}');
										`
									}}
								/>
							</>
						)
					}
					{/* Google Tag Manager */}
					{gtmID && (
						<>
							{/* GTM Script */}
							<script
								dangerouslySetInnerHTML={{
									__html: `
									(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
									new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
									j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
									'https://www.googletagmanager.com/gtm.js?id=' + i + dl;f.parentNode.insertBefore(j,f);
								})(window,document,'script','dataLayer','${gtmID}');
								`
								}}
							/>
						</>
					)}

					{/* Facebook Pixel */}
					{pixelID && (
						<>
							{/* Facebook Pixel Script */}
							<script
								dangerouslySetInnerHTML={{
									__html: `
									!function(f,b,e,v,n,t,s)
									{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
									n.callMethod.apply(n,arguments):n.queue.push(arguments)};
									if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
									n.queue=[];t=b.createElement(e);t.async=!0;
									t.src=v;s=b.getElementsByTagName(e)[0];
									s.parentNode.insertBefore(t,s)}(window, document,'script',
									'https://connect.facebook.net/en_US/sdk.js');
									fbq('init', '${pixelID}');
									fbq('track', 'PageView');
								`
								}}
							/>
						</>
					)}
				</Head>
			)}
			<CheckoutPage
				query={{ ...getQuery(props.router.query), ...props.themeQuery }}
				isReady={props.router.isReady}
				eventData={props.eventData}
			/>
		</>
	);
};

export async function getServerSideProps(context) {
	let eventData = false;
	const themeQuery = {};
	try {
		if (eventUrlValidation(context.params.eventUrl.toLowerCase())) {
			const res = await getEventDetails(context.params.eventUrl.toLowerCase());
			if (res.status === 200) {
				eventData = res.data;
				if (eventData.event_template === 1 || eventData.event_template === 3) {
					if (themeQuery.btnBg === undefined) {
						themeQuery.btnBg = eventData.event_template === 1 ? "fdb14b" : "000";
					}
					if (themeQuery.anchorTag === undefined) {
						themeQuery.anchorTag = eventData.event_template === 1 ? "fdb14b" : "blue";
					}
					if (themeQuery.borderCl === undefined) {
						themeQuery.borderCl = eventData.event_template === 1 ? "EFE9ED" : "cacaca";
					}
					if (themeQuery.tertiaryBg === undefined) {
						themeQuery.tertiaryBg = "141b25";
					}
					if (themeQuery.secondaryCl === undefined) {
						themeQuery.secondaryCl = "141b25";
					}
					if (themeQuery.secondaryBg === undefined) {
						themeQuery.secondaryBg = "f6f6fc";
					}
				}
			}
		}
	} catch (err) {
		console.log(err);
	}

	if (!eventData) {
		return {
			redirect: {
				destination: "/error/404",
				permanent: false
			}
		};
	}

	return {
		props: {
			eventData,
			themeQuery
		}
	};
}

export default withRouter(Checkout);
